import { useEffect, useState } from "react";
import type { Transaction } from "../../types";
import { dateLabel, money } from "../../lib/finance";

/**
 * A new bill's optional starting point: an existing transaction it is based on, recorded as its
 * first paid cycle. Shows the chosen one with a way to change it, or a search over transactions
 * that are not already part of a bill.
 */
export default function BillSourcePicker({ transactions, searchLedger, source, onPick, onClear }: {
  transactions: Transaction[];
  /** Loads the whole ledger: a search can match an entry of any age. */
  searchLedger: () => void;
  source: Transaction | undefined;
  onPick: (t: Transaction) => void;
  onClear: () => void;
}) {
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const searching = q.length > 1;
  useEffect(() => {
    if (searching) searchLedger();
  // `searchLedger` is recreated each render; asking once the search begins is enough.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searching]);
  const pickable = searching
    ? transactions
        .filter((t) => !t.allocations && !t.currencyConversion && !t.billId && t.type.toLowerCase() !== "transfer" && (t.title || t.description || "").toLowerCase().includes(q))
        .sort((a, b) => b.date.localeCompare(a.date))
        .slice(0, 6)
    : [];

  if (source)
    return (
      <div className="linked-txn-list">
        <div className="linked-txn-row">
          <span className="linked-txn-main">
            <span>
              <strong>Based on {source.title || source.description || "this transaction"}</strong>
              <small>{dateLabel(source.date)} · {money(source.amount, source.currency)}</small>
            </span>
          </span>
          <button type="button" className="text-button" onClick={onClear}>Change</button>
        </div>
      </div>
    );
  return (
    <label>
      <span className="field-label">Base this on an existing transaction <small>Optional</small></span>
      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by title or description"
        autoComplete="off"
      />
      {pickable.length > 0 && (
        <div className="linked-txn-list">
          {pickable.map((t) => (
            <div className="linked-txn-row" key={t.id}>
              <button type="button" className="linked-txn-main" onClick={() => { onPick(t); setSearch(""); }}>
                <span>
                  <strong>{t.title || t.description}</strong>
                  <small>{dateLabel(t.date)}</small>
                </span>
                <span>{money(t.amount, t.currency)}</span>
              </button>
            </div>
          ))}
        </div>
      )}
    </label>
  );
}
